'use strict'

/**
 * マトリクス画面の派生データ（derived state）をストアのスライスから 1 回だけ算出するフック。 ストアには「素のデータ」だけを置き、ここで useMemo
 * によって絞り込み・集計・事前計算をまとめる（state-management.md §6: 導出可能な値は state に持たない）。
 *
 * パフォーマンス設計（コードレビュー反映）:
 *
 * - 最重量の statusByCode（列コード→マッピング状態。大きな mappingList の全走査）は useStatusByCode 経由で Web Worker に逃がす。 依存は mapping/period
 *   のみなので、検索・モード・行フィルタでは再計算されず参照も安定する（メモ化行が再レンダーをスキップできる）。 Worker 非対応環境では同期計算にフォールバックする。
 * - 列フィルタ・行フィルタ・サマリは軽量（O(列) / O(行)）かつ粒度の細かいメモ化が効くため、メインスレッドの useMemo に残す。 それぞれ必要な依存だけを持たせ、無関係な変更（例: モード変更で rowFilters
 *   をリセット）で列の絞り込みが再計算されないようにする。
 */

import { useMemo } from 'react'
import {
  filterLeftColumnByModeAndPeriod,
  filterDetailPeriodByKind,
  sortDetailPeriodTasksByPeriodValueDesc,
  flattenHorizontalColumns,
  collectAllColumnCodes,
  filterHorizontalColumns,
  filterLeftColumnByRowFiltersFromStatus,
  computeSummaryFromStatus,
  computeTotals,
  computeRowFilterOptionsFromRf,
  periodTypeForFilter
} from './helpers'
import { useDashboardMatrixSelector } from './store'
import { useStatusByCode } from './useStatusByCode'
import { measureMatrixPerf } from './matrixPerf'

/**
 * @typedef {object} MatrixDerived
 * @property {object[]} leftColumnRows - モード・対象期間で絞り込んだ左カラム行
 * @property {object[]} detailPeriodOptions - 詳細期間ドロップダウン候補（新しい順）
 * @property {object[]} flatColumns - 横軸のフラットな列リスト
 * @property {{ [code: string]: 'set' | 'unset' | 'remove' }} statusByCode - 列コード→マッピング状態（事前計算）
 * @property {object[]} filteredColumns - 検索・設定済/未設定で絞り込んだ列
 * @property {object[]} leftColumnRowsFiltered - 行フィルタ適用後の左カラム行
 * @property {object} summary - サマリ集計（表示中の行・列に基づく）
 * @property {boolean} deriving - StatusByCode を Worker で計算中かどうか（オーバーレイ表示用）
 * @property {object} totals - 総数（モード・対象期間のみ反映）
 * @property {Map<string, string>} codeToName - 列コード→表示名
 * @property {{ major: string[]; middle: string[]; dashboard: string[]; graph: string[] }} rowFilterOptions - 行フィルタ候補
 */

/**
 * ストアの素データから派生データ一式を算出する。
 *
 * @returns {MatrixDerived}
 */
export function useMatrixDerived() {
  const mode = useDashboardMatrixSelector((s) => s.mode)
  const periodKind = useDashboardMatrixSelector((s) => s.periodKind)
  const leftColumnRowsFull = useDashboardMatrixSelector((s) => s.leftColumnRowsFull)
  const horizontalBlocks = useDashboardMatrixSelector((s) => s.horizontalBlocks)
  const taskListFull = useDashboardMatrixSelector((s) => s.taskListFull)
  const mappingList = useDashboardMatrixSelector((s) => s.mappingList)
  const mappingListMonthly = useDashboardMatrixSelector((s) => s.mappingListMonthly)
  const searchText = useDashboardMatrixSelector((s) => s.filters.searchText)
  const checkedSet = useDashboardMatrixSelector((s) => s.filters.checkedSet)
  const checkedUnset = useDashboardMatrixSelector((s) => s.filters.checkedUnset)
  const rowFilters = useDashboardMatrixSelector((s) => s.filters.rowFilters)

  const periodType = periodTypeForFilter(periodKind)

  const leftColumnRows = useMemo(
    () => filterLeftColumnByModeAndPeriod(leftColumnRowsFull, mode, periodType),
    [leftColumnRowsFull, mode, periodType]
  )

  const detailPeriodOptions = useMemo(
    () => sortDetailPeriodTasksByPeriodValueDesc(filterDetailPeriodByKind(taskListFull, periodKind)),
    [taskListFull, periodKind]
  )

  const flatColumns = useMemo(() => {
    const blocks = Array.isArray(horizontalBlocks) ? horizontalBlocks : []
    return flattenHorizontalColumns(blocks)
  }, [horizontalBlocks])

  // 事前計算: 横軸の全コード + 各行 required_columns について、マッピング状態を 1 回でまとめるための対象コード集合。
  const allColumnCodes = useMemo(
    () => collectAllColumnCodes(flatColumns, leftColumnRowsFull),
    [flatColumns, leftColumnRowsFull]
  )

  // 最重量の statusByCode 計算の入力を安定参照化する。mapping/period のみに依存し、
  // モード・検索・行フィルタには依存しない → これらの操作では Worker への再送信も再計算も起きない。
  const statusInput = useMemo(
    () => ({ allColumnCodes, mappingList, mappingListMonthly, periodKind }),
    [allColumnCodes, mappingList, mappingListMonthly, periodKind]
  )

  // Web Worker（フォールバック: 同期）で statusByCode を算出する。参照は mapping/period が変わらない限り安定。
  const { statusByCode, deriving } = useStatusByCode(statusInput)

  // 列の検索・設定済/未設定フィルタは空 mapping_type のエッジケース挙動を保つため従来関数を使用。
  // 依存は列フィルタに関係する項目のみ（rowFilters は列に無関係）。これによりモード変更で rowFilters を
  // リセットしても filteredColumns の参照が安定し、メモ化された行の再レンダーをスキップできる。
  const filteredColumns = useMemo(
    () =>
      measureMatrixPerf('filterHorizontalColumns', () =>
        filterHorizontalColumns(
          flatColumns,
          mappingList,
          { searchText, checkedSet, checkedUnset },
          mappingListMonthly,
          periodKind
        )
      ),
    [flatColumns, mappingList, mappingListMonthly, periodKind, searchText, checkedSet, checkedUnset]
  )

  const leftColumnRowsFiltered = useMemo(
    () =>
      measureMatrixPerf('filterLeftColumnByRowFilters', () =>
        filterLeftColumnByRowFiltersFromStatus(leftColumnRows, rowFilters, statusByCode)
      ),
    [leftColumnRows, rowFilters, statusByCode]
  )

  const summary = useMemo(
    () =>
      measureMatrixPerf('computeSummary', () =>
        computeSummaryFromStatus(leftColumnRowsFiltered, filteredColumns, statusByCode)
      ),
    [leftColumnRowsFiltered, filteredColumns, statusByCode]
  )

  const totals = useMemo(() => computeTotals(leftColumnRows, flatColumns), [leftColumnRows, flatColumns])

  const codeToName = useMemo(() => {
    const m = new Map()
    for (const c of flatColumns) m.set(c.column_code, c.column_name || c.column_code)
    return m
  }, [flatColumns])

  const rowFilterOptions = useMemo(
    () => computeRowFilterOptionsFromRf(leftColumnRows, rowFilters),
    [leftColumnRows, rowFilters]
  )

  return {
    leftColumnRows,
    detailPeriodOptions,
    flatColumns,
    statusByCode,
    filteredColumns,
    leftColumnRowsFiltered,
    summary,
    deriving,
    totals,
    codeToName,
    rowFilterOptions
  }
}
